import {
  Button,
  Flex,
  Header,
  Modal,
  SelectionTile,
  SimpleGrid,
  styled,
  Text,
} from '@driftt/dds'
import React, { useState } from 'react'

import { SCENARIO_TEMPLATES } from 'constants/scenarios'
import BoltLogo from 'images/bolt-logo'

const TemplateTile = styled(SelectionTile.Item, {
  alignItems: 'flex-start',
  flexGrow: 'unset',
  minHeight: '204px',
  textAlign: 'left',
  gap: '$1',
})

const ScenarioTemplateCard = ({
  id,
  name,
  description,
  driftManaged,
  isDisabled,
  dataSelected,
}: {
  id: string
  name: string
  description: string
  driftManaged?: boolean
  isDisabled?: boolean
  dataSelected?: boolean
}) => (
  <TemplateTile
    id={id}
    isDisabled={isDisabled}
    data-selected={dataSelected}
    css={{
      position: 'relative',
      ...(isDisabled && {
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          zIndex: 1,
        },
      }),
    }}
  >
    <Flex direction="row" alignItems="center" gap={0}>
      <Header variant="h3" style={{ marginRight: '6px' }}>
        {name}
      </Header>
      {driftManaged && <BoltLogo height="14" width="12" />}
    </Flex>
    <Text>{description}</Text>
  </TemplateTile>
)
const ScenarioTemplatesModal = ({
  isOpen,
  onClose,
  onAction,
  templatesUsed = [],
}: {
  isOpen: boolean
  onClose: () => void
  onAction: (templateId: string) => void
  templatesUsed?: string[]
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [selectPreviouslyUsedTemplate, setSelectPreviouslyUsedTemplate] =
    useState('')

  const createScenarios = SCENARIO_TEMPLATES.filter(
    (template) => !templatesUsed.includes(template.id)
  )

  const previouslyUsedScenarios = SCENARIO_TEMPLATES.filter(
    (template) =>
      templatesUsed.includes(template.id) && template.id !== 'CUSTOM'
  )
  const handleSelectTemplate =
    (isPreviouslyUsed: boolean) => (templateId: string) => {
      if (isPreviouslyUsed) {
        setSelectedTemplate('')
        setSelectPreviouslyUsedTemplate(templateId)
      } else {
        setSelectPreviouslyUsedTemplate('')
        setSelectedTemplate(templateId)
      }
    }

  const isDisabled = (templateId: string) => {
    return templatesUsed.includes(templateId)
  }

  return (
    <Modal.Root open={isOpen} onClose={onClose}>
      <Modal.Dialog fullWidth size="x-large" variant="info">
        <Modal.Title
          title="Create scenario"
          subtitle="Start with a template or create your own from scratch:"
        />
        <Modal.Content>
          <div style={{ marginBottom: '24px' }}>
            <SelectionTile.Group
              as={SimpleGrid}
              gap={1}
              columns={3}
              onChange={handleSelectTemplate(false)}
              data-selected={false}
            >
              {createScenarios.map((template, i) => (
                <ScenarioTemplateCard
                  {...template}
                  key={template.id}
                  id={template.id}
                  isDisabled={false}
                  dataSelected={template.id === selectedTemplate}
                />
              ))}
            </SelectionTile.Group>
          </div>
          {previouslyUsedScenarios.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <Text variant="subtitle2" css={{ marginBottom: '10px' }}>
                Previously used
              </Text>
              <SelectionTile.Group
                as={SimpleGrid}
                gap={1}
                columns={3}
                onChange={handleSelectTemplate(true)}
                unselectable="on"
              >
                {previouslyUsedScenarios.map((template, i) => (
                  <ScenarioTemplateCard
                    {...template}
                    key={template.id}
                    id={template.id}
                    isDisabled={isDisabled(template.id)}
                    dataSelected={template.id === selectPreviouslyUsedTemplate}
                  />
                ))}
              </SelectionTile.Group>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() =>
              onAction(selectedTemplate || selectPreviouslyUsedTemplate)
            }
            disabled={
              selectPreviouslyUsedTemplate === '' && selectedTemplate === ''
            }
          >
            Create
          </Button>
        </Modal.Actions>
      </Modal.Dialog>
    </Modal.Root>
  )
}

export default ScenarioTemplatesModal
